/** Column definitions for the batch tenant-config grid.
 *
 * Rows are client-derived (`buildBatchRows`) and static per data load — the
 * only live cell is the pending-count badge, which subscribes to the
 * change-set store itself so edits never touch the row objects.
 */
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';

import { usePendingByCanonicalKey } from './ChangeSetContext';
import type { BatchRow } from './configIndex';

/** Live pending-edit count for one canonical key. Subscribes to the
 *  change-set context directly — the grid's rows stay untouched by edits. */
function PendingBadgeCell({ canonicalKey }: { canonicalKey: string }) {
  const pending = usePendingByCanonicalKey();
  const count = pending.get(canonicalKey) ?? 0;
  if (count === 0) return null;
  return <Chip label={count} size="small" color="warning" />;
}

export function buildColumns(tenantCount: number): GridColDef<BatchRow>[] {
  return [
    {
      field: 'key',
      headerName: 'Key',
      flex: 2,
      minWidth: 260,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            title={row.key}
            sx={{ fontFamily: 'monospace', fontSize: 13 }}
          >
            {row.key}
          </Typography>
          {row.group !== 'core' && <Chip label={row.group} size="small" variant="outlined" />}
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 140,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography variant="body2" noWrap title={row.type}>
            {row.type}
          </Typography>
          {row.secret && <Chip label="secret" size="small" color="warning" variant="outlined" />}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 3,
      minWidth: 240,
      renderCell: ({ row }) =>
        row.description ? (
          <Tooltip title={row.description}>
            <Typography variant="body2" noWrap color="text.secondary">
              {row.description}
            </Typography>
          </Tooltip>
        ) : null,
    },
    {
      field: 'definedCount',
      headerName: 'Defined',
      width: 110,
      renderCell: ({ row }) => `${row.definedCount} / ${tenantCount}`,
    },
    {
      field: 'distinctCount',
      headerName: 'Distinct',
      width: 90,
      renderCell: ({ row }) => (row.secret ? '—' : row.distinctCount),
    },
    {
      field: 'pending',
      headerName: 'Pending',
      width: 90,
      sortable: false,
      renderCell: ({ row }) => <PendingBadgeCell canonicalKey={row.id} />,
    },
  ];
}
